import { useCallback, useMemo } from 'react';
import {
  Alert,
  AlertAppearances,
  AlertOrientations,
  AlertVariants,
  Checkbox,
  Dropdown,
  Field,
  FieldTypes,
  Typography,
  TypographyColors,
  TypographyVariants,
  TypographyWeights,
  useFormApi,
  useFormState,
} from '@carboledger/ui';
import { EMPTY_LIST } from '@/lib/utils';
import { TextField } from '@/components/common';
import { useTranslations } from '@/hooks';
import {
  MB_PERIOD_COUNTS,
  MB_PERIOD_FIELDS,
  MIN_MB_PERIOD_LENGTH_DAYS,
  emptyMbPeriodDetailValues,
  mbPeriodKeys,
} from '../../constants/sites.constants';
import { MbPeriodCard } from './MbPeriodCard';
import styles from './MbPeriodModalFields.module.scss';

// The checkbox is locked, so its change never fires; Checkbox still expects the prop.
const NOOP = () => {};

const MbPeriodCountField = ({ disabled = false }) => {
  const t = useTranslations();
  const { values } = useFormState();
  const { setValue } = useFormApi();

  const value = values[MB_PERIOD_FIELDS.numberOfPeriods];
  const periodKeys = values[MB_PERIOD_FIELDS.periodKeys] ?? EMPTY_LIST;

  const options = useMemo(
    () => MB_PERIOD_COUNTS.map((count) => ({ value: count, label: count })),
    [],
  );

  const handleChange = useCallback(
    (event) => {
      const count = event.target.value;
      const nextKeys = mbPeriodKeys(count);

      nextKeys
        .filter((periodKey) => !periodKeys.includes(periodKey))
        .forEach((periodKey) =>
          Object.entries(emptyMbPeriodDetailValues(periodKey)).forEach(([field, fieldValue]) =>
            setValue(field, fieldValue),
          ),
        );

      setValue(MB_PERIOD_FIELDS.numberOfPeriods, count);
      setValue(MB_PERIOD_FIELDS.periodKeys, nextKeys);
    },
    [periodKeys, setValue],
  );

  return (
    <Field name={MB_PERIOD_FIELDS.numberOfPeriods} required>
      {({ id, onBlur, status, message, required }) => (
        <Dropdown
          id={id}
          name={MB_PERIOD_FIELDS.numberOfPeriods}
          label={t('FORM_MB_PERIOD_COUNT')}
          placeholder={t('FORM_MB_PERIOD_COUNT_PLACEHOLDER')}
          value={value ?? ''}
          onChange={handleChange}
          onBlur={onBlur}
          status={status}
          message={message}
          required={required}
          disabled={disabled}
          options={options}
        />
      )}
    </Field>
  );
};

/**
 * Props:
 *   terminalOptions  the site's terminals
 *   terminalsLocked  true in edit mode — the period belongs to one terminal
 *   errorKey         i18n key for the pre-save validation message
 *   saving           locks every control while the save is in flight
 */
export const MbPeriodModalFields = ({ terminalOptions, terminalsLocked, errorKey, saving }) => {
  const t = useTranslations();
  const { values } = useFormState();

  const periodKeys = values[MB_PERIOD_FIELDS.periodKeys] ?? EMPTY_LIST;

  return (
    <div className={styles.fields}>
      {errorKey && (
        <Alert
          variant={AlertVariants.danger}
          appearance={AlertAppearances.accent}
          orientation={AlertOrientations.horizontal}
        >
          {t(errorKey, { days: MIN_MB_PERIOD_LENGTH_DAYS })}
        </Alert>
      )}

      <TextField disabled name={MB_PERIOD_FIELDS.siteName} label={t('FIELD_SITE_NAME')} />

      <Field name={MB_PERIOD_FIELDS.terminalIds} type={FieldTypes.multi} required>
        {({ id, name, value, onChange, status, message, required }) => (
          <Dropdown
            multiple
            id={id}
            name={name}
            label={t('FIELD_TERMINAL_NAME')}
            placeholder={t('FORM_SELECT_TERMINAL')}
            emptyMessage={t('FORM_TERMINAL_EMPTY')}
            value={value}
            onChange={onChange}
            status={status}
            message={message}
            required={required}
            disabled={terminalsLocked || saving}
            options={terminalOptions}
          />
        )}
      </Field>

      <div className={styles.sectionDivider}>
        <Typography variant={TypographyVariants.caption} color={TypographyColors.secondary}>
          {t('SECTION_TIME_PERIOD')}
        </Typography>
        <span className={styles.sectionLine} aria-hidden="true" />
      </div>

      <MbPeriodCountField disabled={saving} />

      {periodKeys.length > 0 && (
        <div className={styles.periods}>
          <Typography
            variant={TypographyVariants.label}
            weight={TypographyWeights.semibold}
            color={TypographyColors.secondary}
          >
            {t('FORM_MB_PERIOD_DATES')}
          </Typography>

          {periodKeys.map((periodKey) => (
            <MbPeriodCard key={periodKey} periodKey={periodKey} />
          ))}
        </div>
      )}

      <div className={styles.configuration}>
        <Typography
          variant={TypographyVariants.label}
          weight={TypographyWeights.semibold}
          color={TypographyColors.secondary}
        >
          {t('SECTION_CONFIGURATION')}
        </Typography>
        {/* Not a setting: balances always carry forward, so it is shown on and locked. */}
        <Checkbox label={t('FORM_MB_PERIOD_CARRY_FORWARD')} checked onChange={NOOP} disabled />
      </div>
    </div>
  );
};
